import React from 'react';
import Box from '@mui/material/Box';

export interface HtmlPreviewProps {
  /** The template body HTML to render. Tokens like {{client.firstName}} render literally. */
  html: string;
  /** data-testid for the wrapping element, so callers can target their specific preview. */
  testId?: string;
  /** Minimum rendered height in px (default 320). Ignored when `fill` is set. */
  minHeight?: number;
  /** Fill the parent's height (100%) instead of a fixed minHeight — for the side-by-side editor. */
  fill?: boolean;
}

/**
 * Renders author-supplied email HTML as a live visual preview.
 *
 * Security: the HTML is rendered inside an iframe with an EMPTY `sandbox` attribute.
 * An empty sandbox grants NO capabilities — crucially no `allow-scripts` — so any
 * <script>, onerror, or other active content in a template body cannot execute and is
 * isolated from the parent document. This is the safe way to render untrusted-ish HTML;
 * we deliberately avoid dangerouslySetInnerHTML, which would run scripts in our origin.
 */
const HtmlPreview: React.FC<HtmlPreviewProps> = ({ html, testId, minHeight = 320, fill = false }) => (
  <Box
    data-testid={testId}
    sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      overflow: 'hidden',
      backgroundColor: '#ffffff',
      display: 'flex',
      // `flex: 1` and NOT `height: '100%'` when filling. A percentage height on a flex item
      // resolves against the container's DEFINITE height; once the container's own height itself
      // comes from `flex: 1` several levels up (Paper → DialogContent → Stack → Box → here), that
      // definiteness is not guaranteed and the frame collapses to its intrinsic size — which is
      // exactly the short iframe in a tall dialog. Taking the height from flex layout instead
      // makes this box's height definite for its own child, so the iframe's 100% resolves.
      ...(fill ? { flex: 1, minHeight: 0 } : { minHeight }),
    }}
  >
    <iframe
      title="Email preview"
      data-testid={testId ? `${testId}-frame` : 'html-preview-frame'}
      // Empty sandbox = no scripts, no same-origin — see the security note above.
      sandbox=""
      srcDoc={html || ''}
      style={fill
        ? { width: '100%', height: '100%', border: 'none', flex: 1 }
        : { width: '100%', minHeight, border: 'none', flex: 1 }}
    />
  </Box>
);

export default HtmlPreview;
